import { useMemo, useState, useEffect, useRef } from "react";
import useModal from "@/hooks/useModal";
import DateSelect from "@/components/DateSelect";
import { useForm, Controller, FormProvider } from "react-hook-form";
import Autocomplete from "@/components/Autocomplete";
import MenuDropdown from "@/components/MenuDropdown";
import AvatarDetails from "@/components/AvatarDetails";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { gql, useLazyQuery } from "@apollo/client";
import { STUDENTS_QUERY } from "../../pages/education/universities/[universityId]/students/queries";
import { GET_FACILITIES } from "../../pages/billing/queries";

export type InternshipFormData = {
  studentId?: string;
  facility: string;
  facilityId: string;
  supervisor: string;
  supervisorId: string;
  startDate: { startDate: string | Date; endDate: string | Date };
  endDate: { startDate: string | Date; endDate: string | Date };
};

interface InternshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStudent?: any;
  selectedInternship?: any;
  // Returning a promise lets RHF's isSubmitting track the full mutation
  // lifecycle so the Save button stays disabled until the request settles.
  onSubmit: (data: InternshipFormData) => void | Promise<void>;
  universityId?: string;
  error?: string | null;
  onClearError?: () => void;
}

// Helper function to convert API date string to local midnight YYYY-MM-DD format
// This prevents timezone shifts that cause off-by-one day errors
const localMidnight = (dateString: string) => {
  const localDate = new Date(dateString + "T00:00:00");
  return localDate.toISOString().split("T")[0];
};

export default function InternshipModal({
  isOpen,
  onClose,
  selectedStudent,
  selectedInternship,
  onSubmit,
  universityId,
  error,
  onClearError
}: InternshipModalProps) {
  const { Modal } = useModal();
  const [search, setSearch] = useState("");
  const [facilitySearch, setFacilitySearch] = useState("");

  const [getStudents, { data: studentsData, loading: studentsLoading }] = useLazyQuery(STUDENTS_QUERY, {
    variables: {
      input: {
        universityLegacyIds: universityId ? [universityId] : [],
        alumni: false,
        search: search || undefined
      },
      first: 50
    },
    fetchPolicy: "network-only"
  });

  const [getFacilities, { data: facilitiesData, loading: facilitiesLoading }] = useLazyQuery(GET_FACILITIES, {
    variables: {
      input: {
        search: facilitySearch || undefined
      }
    },
    fetchPolicy: "network-only"
  });

  const FACILITY_EMPLOYEES_QUERY = gql`
    query FacilityEmployees($facilityId: ID!) {
      facility(id: $facilityId) {
        id
        employments(orderBy: JOB_CLASSIFICATION_RANK_ASC, active: true) {
          nodes {
            id
            jobClassification
            person {
              id
              firstName
              lastName
              displayName
              profilePhoto
              pgaId
            }
          }
        }
      }
    }
  `;

  const [supervisors, setSupervisors] = useState<SupervisorOption[]>([]);
  const [getSupervisors, { loading: supervisorsLoading }] = useLazyQuery(FACILITY_EMPLOYEES_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.facility?.employments?.nodes) {
        setSupervisors(data.facility.employments.nodes);
      }
    }
  });

  const isEditMode = !!selectedInternship;

  // Form state. For single-date fields we store { startDate, endDate } with the same ISO date string (YYYY-MM-DD).
  // This matches react-tailwindcss-datepicker expectations and keeps the calendar highlight stable on reopen.
  const methods = useForm<InternshipFormData>({
    defaultValues: {
      studentId: selectedStudent?.id || "",
      facility: selectedInternship?.facility?.name || "",
      facilityId: selectedInternship?.facility?.id || "",
      supervisor: selectedInternship?.supervisor
        ? `${selectedInternship.supervisor.firstName} ${selectedInternship.supervisor.lastName}`
        : "",
      supervisorId: selectedInternship?.supervisor?.id || "",
      // Use local midnight to avoid timezone shifts, then persist as YYYY-MM-DD for the picker
      startDate: selectedInternship?.startedOn
        ? (() => {
            const dateString = localMidnight(selectedInternship.startedOn);
            return { startDate: dateString, endDate: dateString };
          })()
        : { startDate: "", endDate: "" },
      endDate: selectedInternship?.endedOn
        ? (() => {
            const dateString = localMidnight(selectedInternship.endedOn);
            return { startDate: dateString, endDate: dateString };
          })()
        : { startDate: "", endDate: "" }
    },
    mode: "onChange"
  });

  const {
    control,
    reset,
    watch,
    setValue,
    formState: { isSubmitting }
  } = methods;

  // Re-entry guard prevents duplicate internships from rapid double-clicks /
  // Enter-presses that fire before React disables the Save button.
  const submittingRef = useRef(false);

  // Watch form values early so they can be used in useEffects
  const studentIdValue = watch("studentId");
  const facilityValue = watch("facility");
  const facilityIdValue = watch("facilityId");

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen && universityId) {
      getStudents();
      getFacilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, universityId]);

  // Load supervisors when facility changes in edit mode
  useEffect(() => {
    if (isOpen && isEditMode && selectedInternship?.facility?.id) {
      getSupervisors({
        variables: {
          facilityId: selectedInternship.facility.id
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, selectedInternship?.facility?.id]);

  // Load supervisors when facilityId changes (for both edit and create modes)
  useEffect(() => {
    if (isOpen && facilityIdValue && !supervisorsLoading) {
      getSupervisors({
        variables: {
          facilityId: facilityIdValue
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, facilityIdValue]);

  // Reset and populate when editing an existing internship
  useEffect(() => {
    if (selectedInternship && isOpen) {
      // Convert API dates to YYYY-MM-DD strings.
      // Keeping startDate=endDate ensures asSingle mode stays consistent and selected day remains highlighted.
      const startDateValue = selectedInternship.startedOn
        ? (() => {
            const dateString = localMidnight(selectedInternship.startedOn);
            return { startDate: dateString, endDate: dateString };
          })()
        : { startDate: "", endDate: "" };

      const endDateValue = selectedInternship.endedOn
        ? (() => {
            const dateString = localMidnight(selectedInternship.endedOn);
            return { startDate: dateString, endDate: dateString };
          })()
        : { startDate: "", endDate: "" };

      // Reset avoids incremental field updates and keeps RHF state consistent
      reset({
        studentId: selectedStudent?.id || "",
        facility: selectedInternship.facility?.name || "",
        facilityId: selectedInternship.facility?.id || "",
        supervisor: selectedInternship.supervisor
          ? `${selectedInternship.supervisor.firstName} ${selectedInternship.supervisor.lastName}`
          : "",
        supervisorId: selectedInternship.supervisor?.id || "",
        startDate: startDateValue,
        endDate: endDateValue
      });

      setFacilitySearch(selectedInternship.facility?.name || "");
    }
  }, [selectedInternship, selectedStudent, reset, isOpen, setFacilitySearch]);

  const studentOptions = useMemo(() => {
    if (!studentsData?.universityStudents?.nodes) return [];

    return studentsData.universityStudents.nodes.map((student: any) => ({
      id: student.id,
      name: student.displayName,
      pgaId: student.pgaId,
      email: student.email
    }));
  }, [studentsData]);

  const facilityOptions = useMemo(() => {
    if (!facilitiesData?.facilities?.nodes) return [];

    return facilitiesData.facilities.nodes.map((facility: any) => ({
      id: facility.id,
      name: facility.name,
      location: facility.addresses?.[0]
        ? `${facility.addresses[0].address1}, ${facility.addresses[0].city}, ${facility.addresses[0].state}`
        : ""
    }));
  }, [facilitiesData]);

  const filteredStudents = useMemo(() => {
    if (!studentIdValue) return studentOptions;
    const searchLower = studentIdValue.toLowerCase();
    return studentOptions.filter(
      (student) =>
        (student.name?.toLowerCase() || "").includes(searchLower) ||
        (student.pgaId?.toLowerCase() || "").includes(searchLower) ||
        (student.email?.toLowerCase() || "").includes(searchLower)
    );
  }, [studentIdValue, studentOptions]);

  const filteredFacilities = useMemo(() => {
    if (!facilityValue) return facilityOptions;
    const searchLower = facilityValue.toLowerCase();
    return facilityOptions.filter(
      (facility: FacilityOption) =>
        (facility.name?.toLowerCase() || "").includes(searchLower) ||
        (facility.location?.toLowerCase() || "").includes(searchLower)
    );
  }, [facilityValue, facilityOptions]);

  const supervisorOptions = useMemo(() => {
    if (!supervisors) return [];

    return supervisors.map((employment: any) => ({
      id: employment.person.id,
      name: employment.person.displayName,
      title: employment.jobClassification || "No classification",
      facility: facilityValue
    }));
  }, [supervisors, facilityValue]);

  const filteredSupervisors = useMemo(() => {
    if (!facilityValue) return [];
    return supervisorOptions;
  }, [supervisorOptions, facilityValue]);

  const handleStudentSearch = (text: string) => {
    setSearch(text);
    getStudents({
      variables: {
        input: {
          universityLegacyIds: universityId ? [universityId] : [],
          alumni: false,
          search: text || undefined
        },
        first: 50
      }
    });
  };

  const handleFacilitySearch = (text: string) => {
    setFacilitySearch(text);
    getFacilities({
      variables: {
        input: {
          search: text || undefined
        }
      }
    });
  };

  const handleFacilitySelect = (facility: any) => {
    setValue("facility", facility.name);
    setValue("facilityId", facility.id);
    setValue("supervisor", "");
    setValue("supervisorId", "");
    setFacilitySearch(facility.name); // Keep search state in sync
  };

  const handleSubmitForm = async (data: InternshipFormData) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const selectedStudent = studentOptions.find((student) => student.name === data.studentId);
      const studentId = selectedStudent ? selectedStudent.id : data.studentId;

      const formData = {
        ...data,
        studentId: studentId
      };

      await onSubmit(formData);
    } finally {
      submittingRef.current = false;
    }
  };

  const handleCloseModal = () => {
    onClose();
    reset();
    setFacilitySearch("");
    setSearch("");
    setSupervisors([]);
  };

  const handleFacilityChange = (value: string) => {
    setValue("facility", value);
    setValue("facilityId", "");
    setValue("supervisor", "");
    setFacilitySearch(value); // Keep search state in sync
  };

  type FacilityOption = {
    id: string;
    name: string;
    location: string;
  };

  type SupervisorOption = {
    id: string;
    name: string;
    title: string;
    facility: string;
  };

  return (
    <>
      {isOpen && (
        <Modal
          onClosed={handleCloseModal}
          hasFormElements
          displayOverflow
          dismissOnOutsideClick
          headline={isEditMode ? "Edit Internship" : "Add Internship"}
          action={
            <Button
              variant="filled"
              icon="check"
              onClick={methods.handleSubmit(handleSubmitForm)}
              disabled={isSubmitting}
              testId="save-button"
            >
              {isEditMode ? "Update" : "Save"}
            </Button>
          }
          data-testid={isEditMode ? "edit-internship-modal" : "add-internship-modal"}
        >
          <FormProvider {...methods}>
            <form>
              <div className="space-y-6">
                {error && (
                  <Banner
                    variant="error"
                    description={error}
                    inline={true}
                    closable={true}
                    onClose={() => onClearError?.()}
                  />
                )}
                {isEditMode && selectedInternship?.student && (
                  <div className="text-body-medium">
                    <strong>Student:</strong> {selectedInternship.student.firstName}{" "}
                    {selectedInternship.student.lastName}
                  </div>
                )}

                {!selectedStudent && !isEditMode && (
                  <Controller
                    name="studentId"
                    control={control}
                    rules={{ required: "A student selection is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <div>
                        <Autocomplete
                          label="Student"
                          required
                          data={filteredStudents}
                          query={field.value}
                          onChangeText={(value) => {
                            handleStudentSearch(value);
                            setValue("studentId", value);
                          }}
                          onSelect={(student) => {
                            setValue("studentId", student.name);
                          }}
                          renderItem={(student) => (
                            <AvatarDetails
                              title={student.name}
                              text={`PGA ID: ${student.pgaId}`}
                              secondaryText={student.email}
                              icon="person"
                            />
                          )}
                          leadingIcon="search"
                          placeholder="Search students"
                          error={!!error}
                          errorText={error?.message}
                          autofocus={false}
                          loadingResults={studentsLoading}
                          menuClassName="max-h-[300px]"
                          constrainMenuWidth={true}
                        />
                      </div>
                    )}
                  />
                )}

                <Controller
                  name="facility"
                  control={control}
                  rules={{ required: "A facility selection is required" }}
                  render={({ fieldState: { error } }) => (
                    <div>
                      <Autocomplete
                        label="Facility"
                        required
                        data={filteredFacilities}
                        query={facilitySearch}
                        onChangeText={(value) => {
                          handleFacilitySearch(value);
                          handleFacilityChange(value);
                        }}
                        onSelect={(facility) => handleFacilitySelect(facility)}
                        renderItem={(facility: FacilityOption) => (
                          <AvatarDetails title={facility.name} text={facility.location} icon="location_on" />
                        )}
                        leadingIcon="search"
                        placeholder="Search facilities"
                        error={!!error}
                        errorText={error?.message}
                        autofocus={false}
                        loadingResults={facilitiesLoading}
                        supportingText="Contact your PGM program administrator if a facility is missing"
                        menuClassName="max-h-[300px]"
                        constrainMenuWidth={true}
                      />
                    </div>
                  )}
                />

                <Controller
                  name="supervisor"
                  control={control}
                  rules={{
                    required: facilityValue ? "A supervisor selection is required" : false
                  }}
                  render={({ field, fieldState: { error } }) => {
                    const selectedSupervisor = filteredSupervisors.find(
                      (s) => s.id === field.value || s.name === field.value
                    );

                    return (
                      <div>
                        <MenuDropdown<SupervisorOption>
                          label="Supervisor"
                          options={filteredSupervisors}
                          value={selectedSupervisor}
                          onSelect={(supervisor) => {
                            setValue("supervisor", supervisor.name);
                            setValue("supervisorId", supervisor.id);
                          }}
                          getLabel={(supervisor) => supervisor.name}
                          renderItem={(supervisor) => (
                            <AvatarDetails
                              title={supervisor.name}
                              text={supervisor.title}
                              secondaryText={supervisor.facility}
                              icon="person"
                            />
                          )}
                          disabled={!facilityValue}
                          constrainMenuWidth={true}
                        />
                        {error && <div className="text-sys-brand-error text-body-small pt-1 pl-4">{error.message}</div>}
                        {!facilityValue && (
                          <div className="text-body-small pt-1 pl-4 text-sys-brand-on-surface-variant">
                            You must select a facility before choosing a supervisor
                          </div>
                        )}
                      </div>
                    );
                  }}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="startDate"
                    control={control}
                    rules={{
                      validate: {
                        startDateNotNull: (value) =>
                          (value?.startDate !== null && value?.startDate !== "") || "Start date is required"
                      }
                    }}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <div data-testid="start-date-input">
                          <DateSelect
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            label="Start Date *"
                            placeholder="Start Date *"
                            useModal={true}
                            error={!!error}
                            errorMessage={error?.message}
                            useSingleDate={true}
                            // Ensures calendar opens focused on the current value rather than today
                            startFrom={field.value?.startDate ? new Date(field.value.startDate as string) : undefined}
                          />
                        </div>
                      );
                    }}
                  />

                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <div data-testid="end-date-input">
                          <DateSelect
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            label="End Date *"
                            placeholder="End Date *"
                            useModal={true}
                            error={!!error}
                            errorMessage={error?.message}
                            useSingleDate={true}
                            // Ensures calendar opens focused on the current value rather than today
                            startFrom={field.value?.startDate ? new Date(field.value.startDate as string) : undefined}
                          />
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </Modal>
      )}
    </>
  );
}
